import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import AutomationFlowsAPI from '../../api/automationFlows';
import { throwErrorMessage } from '../utils/api';

// Store da feature unificada de Automações. `records` guarda os fluxos da tela/
// lista corrente; `catalog` guarda a paleta por gatilho (carregada uma vez).
export const state = {
  records: [],
  catalog: {},
  meta: { count: 0, current_page: 1 },
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isPublishing: false,
  },
};

export const getters = {
  getAutomationFlows(_state) {
    return _state.records;
  },
  getManualFlows(_state) {
    return _state.records.filter(flow => flow.trigger_type === 'manual');
  },
  getCatalog(_state) {
    return _state.catalog;
  },
  getMeta(_state) {
    return _state.meta;
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  get: async ({ commit }, params = {}) => {
    commit(types.SET_AUTOMATION_FLOW_UI_FLAG, { isFetching: true });
    try {
      const response = await AutomationFlowsAPI.get(params);
      commit(types.SET_AUTOMATION_FLOWS, response.data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AUTOMATION_FLOW_UI_FLAG, { isFetching: false });
    }
  },
  // Carrega TODOS os fluxos para os seletores, não só a primeira página — o
  // laço de paginação vive no client (AutomationFlowsAPI.getAll), porque o
  // bloco "executar fluxo" busca pela API sem passar pelo store e precisa da
  // mesma garantia (issue #663).
  fetchAllForSelect: async ({ commit }, params = {}) => {
    commit(types.SET_AUTOMATION_FLOW_UI_FLAG, { isFetching: true });
    try {
      const registros = await AutomationFlowsAPI.getAll(params);
      commit(types.SET_AUTOMATION_FLOWS, {
        payload: registros,
        meta: { count: registros.length, current_page: 1 },
      });
      return registros;
    } catch (error) {
      throwErrorMessage(error);
      return [];
    } finally {
      commit(types.SET_AUTOMATION_FLOW_UI_FLAG, { isFetching: false });
    }
  },
  // Busca um fluxo único pelo id (endpoint show) — o editor precisa da definition
  // completa, não da lista paginada.
  show: async (_store, id) => {
    const response = await AutomationFlowsAPI.show(id);
    return response.data;
  },
  create: async ({ commit }, flowObj) => {
    commit(types.SET_AUTOMATION_FLOW_UI_FLAG, { isCreating: true });
    try {
      const response = await AutomationFlowsAPI.create(flowObj);
      commit(types.ADD_AUTOMATION_FLOW, response.data);
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_AUTOMATION_FLOW_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_AUTOMATION_FLOW_UI_FLAG, { isUpdating: true });
    try {
      const response = await AutomationFlowsAPI.update(id, updateObj);
      commit(types.EDIT_AUTOMATION_FLOW, response.data);
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    } finally {
      commit(types.SET_AUTOMATION_FLOW_UI_FLAG, { isUpdating: false });
    }
  },
  // A cópia nasce rascunho e inativa (o controller cuida disso) — por isso o
  // ADD_AUTOMATION_FLOW aqui é só otimista: quem chama refaz o fetch para pegar
  // a lista na ordem alfabética certa.
  duplicate: async ({ commit }, id) => {
    commit(types.SET_AUTOMATION_FLOW_UI_FLAG, { isCreating: true });
    try {
      const response = await AutomationFlowsAPI.duplicate(id);
      commit(types.ADD_AUTOMATION_FLOW, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      return null;
    } finally {
      commit(types.SET_AUTOMATION_FLOW_UI_FLAG, { isCreating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_AUTOMATION_FLOW_UI_FLAG, { isDeleting: true });
    try {
      await AutomationFlowsAPI.delete(id);
      commit(types.DELETE_AUTOMATION_FLOW, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AUTOMATION_FLOW_UI_FLAG, { isDeleting: false });
    }
  },
  // Sem catch de propósito: a validação do grafo volta como 422
  // { errors: [...] } com uma mensagem legível POR PROBLEMA, e o
  // throwErrorMessage achataria a lista numa string só — o editor precisa dos
  // itens separados para listá-los. O erro do axios sobe intacto.
  publish: async ({ commit }, id) => {
    commit(types.SET_AUTOMATION_FLOW_UI_FLAG, { isPublishing: true });
    try {
      const response = await AutomationFlowsAPI.publish(id);
      commit(types.EDIT_AUTOMATION_FLOW, response.data);
      return response.data;
    } finally {
      commit(types.SET_AUTOMATION_FLOW_UI_FLAG, { isPublishing: false });
    }
  },
  // Execuções de um fluxo. Não entram em `records` (que é a lista de FLUXOS)
  // nem viram mutation: a aba de execuções é leitura paginada e dona do
  // próprio estado — mesmo padrão do `show`.
  fetchSessions: async (_store, { id, page = 1 } = {}) => {
    try {
      const response = await AutomationFlowsAPI.sessions(id, { page });
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    }
  },
  fetchVersions: async (_store, { id, page = 1 } = {}) => {
    try {
      const response = await AutomationFlowsAPI.versions(id, { page });
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    }
  },
  // Uma versão com o grafo, para a visualização somente leitura. Só LEITURA, e
  // de propósito fora de `records` e sem mutation: o grafo antigo não pode
  // chegar perto do estado que o editor lê. Sem catch: a tela distingue 404
  // (versão de teste/inexistente) do resto pelo status, que o
  // throwErrorMessage achataria.
  fetchVersion: async (_store, { id, versionId }) => {
    const response = await AutomationFlowsAPI.version(id, versionId);
    return response.data;
  },
  // Testar: roda o rascunho numa conversa de teste. O erro sobe cru (o editor
  // precisa distinguir 422 com lista de erros de grafo dos demais).
  testRun: async (_store, { id, mode = 'continue' }) => {
    const response = await AutomationFlowsAPI.testRun({ id, mode });
    return response.data;
  },
  run: async (_store, { id, conversationIds, body = {}, variables = {} }) => {
    try {
      const response = await AutomationFlowsAPI.run({
        id,
        conversationIds,
        body,
        variables,
      });
      return response.data;
    } catch (error) {
      return throwErrorMessage(error);
    }
  },
  // Leitura pontual de uma execução (indicador da conversa). Não toca no
  // state — o chamador decide o que fazer com status/output.
  fetchSession: async (_store, { id, sessionId }) => {
    const response = await AutomationFlowsAPI.session(id, sessionId);
    return response.data;
  },
  fetchCatalog: async ({ commit }) => {
    try {
      const response = await AutomationFlowsAPI.catalog();
      commit(types.SET_AUTOMATION_FLOW_CATALOG, response.data);
    } catch (error) {
      // paleta é auxiliar; falha não deve travar a tela
    }
  },
};

export const mutations = {
  [types.SET_AUTOMATION_FLOW_UI_FLAG](_state, data) {
    _state.uiFlags = { ..._state.uiFlags, ...data };
  },
  [types.SET_AUTOMATION_FLOWS](_state, data) {
    _state.records = data.payload || [];
    _state.meta = data.meta || _state.meta;
  },
  [types.ADD_AUTOMATION_FLOW]: MutationHelpers.create,
  [types.EDIT_AUTOMATION_FLOW]: MutationHelpers.update,
  [types.DELETE_AUTOMATION_FLOW]: MutationHelpers.destroy,
  [types.SET_AUTOMATION_FLOW_CATALOG](_state, data) {
    _state.catalog = data || {};
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
